import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Loader2, AlertCircle } from 'lucide-react';
import { publicFetch } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CART_KEY = 'elevatemn_cart';

const ShopConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sessionId) { setError('No session found'); setLoading(false); return; }

    const poll = async (attempts = 0) => {
      try {
        const res = await publicFetch(`${API}/store/checkout/status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          setOrder(data);
          if (data.payment_status === 'paid') {
            localStorage.removeItem(CART_KEY);
            setLoading(false);
          } else if (attempts < 5) {
            setTimeout(() => poll(attempts + 1), 2000);
          } else {
            setLoading(false);
          }
        } else {
          setError('Order not found');
          setLoading(false);
        }
      } catch {
        setError('Could not verify order');
        setLoading(false);
      }
    };
    poll();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-brand-gold mx-auto mb-3" />
          <p className="text-gray-600">Confirming your order...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-brand-dark mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-4">{error || 'Unable to load order details.'}</p>
          <Link to="/shop" className="text-brand-gold font-medium hover:underline">Return to Shop</Link>
        </div>
      </div>
    );
  }

  const paid = order.payment_status === 'paid';

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-6" data-testid="shop-confirmation-page">
      <div className="mx-auto max-w-lg">
        <div className="text-center mb-8">
          {paid ? (
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          ) : (
            <Loader2 className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-spin" />
          )}
          <h1 className="text-3xl font-bold text-brand-dark mb-2" data-testid="confirmation-title">
            {paid ? 'Order Confirmed!' : 'Processing Payment...'}
          </h1>
          <p className="text-gray-600">
            {paid ? 'Thank you for your purchase! Your order details are below.' : 'Please wait while we confirm your payment.'}
          </p>
        </div>

        {paid && (
          <div className="bg-white border p-6 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500 uppercase tracking-wider">Order Number</span>
              <span className="font-mono font-bold text-brand-dark text-lg" data-testid="order-number">{order.order_number}</span>
            </div>

            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Items</p>
              {(order.items || []).map((item, i) => (
                <div key={i} className="flex justify-between text-sm py-1">
                  <span>{item.product_name}{item.variant_label ? ` (${item.variant_label})` : ''} x{item.quantity}</span>
                  <span className="font-medium">${(item.unit_price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t mt-2 pt-2 space-y-1">
                {order.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-green-600"><span>Discount ({order.discount_code})</span><span>-${order.discount_amount?.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between text-sm text-gray-500"><span>Shipping</span><span>${order.shipping_fee?.toFixed(2)}</span></div>
                <div className="flex justify-between text-lg font-bold"><span>Total</span><span>${order.total?.toFixed(2)}</span></div>
              </div>
            </div>

            {order.shipping_address && (
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Shipping To</p>
                <p className="text-sm">{order.customer_name}</p>
                <p className="text-sm text-gray-600">{order.shipping_address.line1}</p>
                {order.shipping_address.line2 && <p className="text-sm text-gray-600">{order.shipping_address.line2}</p>}
                <p className="text-sm text-gray-600">{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</p>
              </div>
            )}

            <p className="text-sm text-gray-500 text-center pt-2">A confirmation will be sent to <strong>{order.customer_email}</strong></p>
          </div>
        )}

        <div className="text-center mt-8">
          <Link to="/shop" className="inline-flex items-center gap-2 bg-brand-dark text-white px-6 py-3 font-medium hover:bg-brand-dark/90 transition-colors" data-testid="continue-shopping-btn">
            <Package className="w-4 h-4" /> Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ShopConfirmationPage;
